import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

const ManifestoSection = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [direction, setDirection] = useState(1);

  const albumPages = [
    {
      id: 1,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564660/Leonardo_Kino_XL_create_an_image_where_a_hardworking_black_man_1_piqe0f.jpg",
      caption: "Empreendedor nato"
    },
    {
      id: 2,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564666/Leonardo_Kino_XL_create_an_image_where_a_girl_child_light_brow_0_tzwp9n.jpg",
      caption: "Sonhos do futuro"
    },
    {
      id: 3,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564662/Leonardo_Kino_XL_create_an_image_where_a_white_working_woman_i_1_qz8utx.jpg",
      caption: "Força e determinação"
    },
    {
      id: 4,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564438/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_0_tmiu6i.jpg",
      caption: "Experiência que inspira"
    },
    {
      id: 5,
      image: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564659/Leonardo_Kino_XL_create_an_image_where_a_hardworking_black_man_2_jgrd2t.jpg",
      caption: "Conquistas diárias"
    }
  ];

  // Auto flip pages
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isFlipping) {
        nextPage();
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [currentPage, isFlipping]);

  const nextPage = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setDirection(1);
    setCurrentPage((prev) => (prev + 1) % albumPages.length);
    setTimeout(() => setIsFlipping(false), 800);
  };

  const prevPage = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setDirection(-1);
    setCurrentPage((prev) => (prev - 1 + albumPages.length) % albumPages.length);
    setTimeout(() => setIsFlipping(false), 800);
  };

  const goToPage = (index) => {
    if (isFlipping || index === currentPage) return;
    setIsFlipping(true);
    setDirection(index > currentPage ? 1 : -1);
    setCurrentPage(index);
    setTimeout(() => setIsFlipping(false), 800);
  };

  // Variantes para a animação de virar página
  const pageVariants = {
    enter: (direction) => ({
      rotateY: direction > 0 ? 90 : -90,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      rotateY: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1],
      },
    },
    exit: (direction) => ({
      rotateY: direction > 0 ? -90 : 90,
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1],
      },
    }),
  };

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-gray-50/30 to-white">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-20 right-10 w-72 h-72 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
          }}
        />
        <motion.div
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute bottom-10 left-10 w-64 h-64 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Conteúdo de texto */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              <Sparkles size={16} className="text-[#10b981]" />
              <span className="text-sm font-medium text-[#10b981]">
                Nosso Manifesto
              </span>
            </motion.div>

            {/* Título */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl font-bold text-[#2C5E50]"
            >
              OXE, UAI OU{' '}
              <span className="bg-gradient-to-r from-[#10b981] to-[#06b6d4] bg-clip-text text-transparent">
                BAH
              </span>
            </motion.h2>

            {/* Descrição */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg text-gray-600 leading-relaxed"
            >
              Sendo brasileiro, você é um empreendedor nato. É para isso que 
              estamos aqui: impulsionar o potencial do seu negócio. Então chega 
              mais! Pegue um café e assista ao nosso vídeo-manifesto.
            </motion.p>

            {/* Botão CTA */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold text-[#2C5E50] overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
                boxShadow: '0 10px 30px rgba(190, 242, 100, 0.4)',
              }}
            >
              {/* Efeito de brilho */}
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 0.6 }}
              />
              
              <span className="relative z-10 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#2C5E50] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={18} className="text-white ml-0.5" fill="white" />
                </span>
                Assistir manifesto
              </span>
              
              <motion.svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="relative z-10"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </motion.svg>
            </motion.button>

            {/* Indicadores de página */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="flex items-center gap-3 pt-4"
            >
              <span className="text-sm text-gray-500">Histórias de sucesso:</span>
              <div className="flex gap-2">
                {albumPages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToPage(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      currentPage === index
                        ? 'bg-[#10b981] w-8'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Álbum de fotos */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[500px] md:h-[600px] flex items-center justify-center"
            style={{ perspective: '1500px' }}
          >
            {/* Sombra do álbum */}
            <div 
              className="absolute bottom-10 left-1/2 -translate-x-1/2 w-[80%] h-8 rounded-full"
              style={{
                background: 'radial-gradient(ellipse, rgba(0,0,0,0.2) 0%, transparent 70%)',
                filter: 'blur(10px)',
              }}
            />

            {/* Container do álbum */}
            <div 
              className="relative w-full max-w-[400px] h-[450px]"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Capa do álbum (fundo) */}
              <div 
                className="absolute inset-0 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #1a3d35 0%, #0f2620 100%)',
                  transform: 'translateZ(-20px)',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
                }}
              />

              {/* Lombada do álbum */}
              <div 
                className="absolute left-0 top-0 bottom-0 w-4 rounded-l-2xl"
                style={{
                  background: 'linear-gradient(90deg, #0f2620 0%, #1a3d35 100%)',
                  transform: 'translateZ(-10px)',
                }}
              />

              {/* Páginas do álbum */}
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentPage}
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="absolute inset-2 rounded-xl overflow-hidden"
                  style={{
                    transformStyle: 'preserve-3d',
                    backfaceVisibility: 'hidden',
                  }}
                >
                  {/* Página do álbum */}
                  <div 
                    className="relative w-full h-full rounded-xl overflow-hidden"
                    style={{
                      background: '#fafafa',
                      boxShadow: `
                        inset 0 0 30px rgba(0,0,0,0.05),
                        0 10px 40px rgba(0,0,0,0.2)
                      `,
                    }}
                  >
                    {/* Textura de papel */}
                    <div 
                      className="absolute inset-0 opacity-30 pointer-events-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                      }}
                    />

                    {/* Moldura da foto */}
                    <div className="absolute inset-6 md:inset-8">
                      {/* Sombra da foto */}
                      <div 
                        className="absolute inset-0 rounded-xl"
                        style={{
                          background: 'rgba(0,0,0,0.1)',
                          transform: 'translate(8px, 8px)',
                          filter: 'blur(15px)',
                        }}
                      />

                      {/* Container da foto */}
                      <motion.div 
                        className="relative w-full h-full rounded-xl overflow-hidden"
                        style={{
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        }}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Foto */}
                        <img
                          src={albumPages[currentPage].image}
                          alt={albumPages[currentPage].caption}
                          className="w-full h-full object-cover"
                        />

                        {/* Overlay gradiente */}
                        <div 
                          className="absolute inset-0"
                          style={{
                            background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.6) 100%)',
                          }}
                        />

                        {/* Caption */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="absolute bottom-4 left-4 right-4"
                        >
                          <p className="text-white font-medium text-lg">
                            {albumPages[currentPage].caption}
                          </p>
                          <p className="text-white/70 text-sm mt-1">
                            {currentPage + 1} / {albumPages.length}
                          </p>
                        </motion.div>

                        {/* Efeito de brilho na foto */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent"
                          animate={{
                            opacity: [0.3, 0.5, 0.3],
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        />
                      </motion.div>

                      {/* Cantos da moldura (estilo polaroid) */}
                      <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-[#10b981]/30 rounded-tl-lg" />
                      <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-[#10b981]/30 rounded-tr-lg" />
                      <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-[#10b981]/30 rounded-bl-lg" />
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-[#10b981]/30 rounded-br-lg" />
                    </div>

                    {/* Número da página */}
                    <div className="absolute bottom-3 right-4 text-gray-400 text-sm font-medium">
                      Página {currentPage + 1}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Botões de navegação */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={prevPage}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-[#2C5E50] hover:bg-[#10b981] hover:text-white transition-colors z-20"
                style={{
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                }}
              >
                <ChevronLeft size={24} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={nextPage}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-[#2C5E50] hover:bg-[#10b981] hover:text-white transition-colors z-20"
                style={{
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                }}
              >
                <ChevronRight size={24} />
              </motion.button>

              {/* Páginas empilhadas (efeito de profundidade) */}
              {[1, 2, 3].map((offset) => (
                <div
                  key={offset}
                  className="absolute inset-2 rounded-xl pointer-events-none"
                  style={{
                    background: '#f5f5f5',
                    transform: `translateZ(${-offset * 3}px) translateX(${offset * 2}px)`,
                    opacity: 1 - offset * 0.2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  }}
                />
              ))}
            </div>

            {/* Decorações flutuantes */}
            <motion.div
              animate={{ 
                y: [0, -15, 0],
                rotate: [0, 5, 0],
              }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-10 right-10 w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
              }}
            >
              <Sparkles className="text-white" size={28} />
            </motion.div>

            <motion.div
              animate={{ 
                y: [0, 10, 0],
                rotate: [0, -5, 0],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="absolute bottom-20 left-5 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
                boxShadow: '0 8px 25px rgba(190, 242, 100, 0.4)',
              }}
            >
              <span className="text-[#2C5E50] text-xl">✨</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ManifestoSection;